import { useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import BatchPhotosImport from "@/components/BatchPhotosImport";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, AlertTriangle, CheckCircle, Images } from "lucide-react";
import { formatMoney } from "@/lib/currency";

const CSV_COLUMNS = ["title", "description", "sku", "collection", "fabric", "brand", "color", "type", "style", "measurements", "weight", "purchase_cost", "shipping_cost", "profit_margin", "stage"];

function CsvImport() {
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [skipInvalid, setSkipInvalid] = useState(true);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/inventory/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "could not parse csv.");
    } finally { setUploading(false); }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const { data } = await api.post("/inventory/import/commit", { preview_id: preview.preview_id, skip_invalid: skipInvalid });
      toast.success(`imported ${data.created} pieces · skipped ${data.skipped}.`);
      setPreview(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "import failed.");
    } finally { setCommitting(false); }
  };

  const sampleCsv = `title,sku,collection,brand,color,type,style,fabric,measurements,weight,purchase_cost,shipping_cost,profit_margin,stage\nivory linen blouse,MP-001,Spring Capsule,Sézane,Cream,Blouse,Parisian,100% Linen,"S — bust 86cm",0.22,18,5,22,Listing & Marketing\nolive trench coat,MP-002,Outerwear Edit,A.P.C.,Muted Olive,Outerwear,Vintage,Cotton gabardine,"M — chest 96cm",1.4,45,12,60,Sourcing & Acquisition`;
  const downloadSample = () => {
    const blob = new Blob([sampleCsv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "maison-pilet-import-template.csv";
    a.click();
  };

  if (preview) {
    return (
      <section data-testid="preview-section">
        <div className="bg-linen border border-taupe/50 p-6 mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid="preview-summary">
          <div>
            <p className="editorial-label">preview · {preview.total} rows</p>
            <p className="font-serif text-2xl lowercase text-charcoal mt-1"><CheckCircle className="w-4 h-4 inline text-brass" /> {preview.valid} ready · <AlertTriangle className="w-4 h-4 inline text-red-700 ml-3" /> {preview.errors.length} with issues</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-xs text-charcoal flex items-center gap-2"><input type="checkbox" checked={skipInvalid} onChange={(e) => setSkipInvalid(e.target.checked)} data-testid="skip-invalid" /> skip rows with errors</label>
            <button onClick={() => setPreview(null)} className="border border-taupe text-charcoal px-5 py-2.5 text-xs tracking-[0.25em] uppercase">cancel</button>
            <button onClick={commit} disabled={committing} data-testid="commit-import" className="bg-brass hover:bg-espresso text-ivory px-5 py-2.5 text-xs tracking-[0.25em] uppercase disabled:opacity-50">
              {committing ? "importing…" : "confirm import"}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto border border-taupe/40">
          <table className="w-full text-xs">
            <thead className="bg-linen">
              <tr className="text-left">
                <th className="editorial-label px-3 py-3">#</th>
                <th className="editorial-label px-3 py-3">title</th>
                <th className="editorial-label px-3 py-3">brand</th>
                <th className="editorial-label px-3 py-3">cost</th>
                <th className="editorial-label px-3 py-3">margin</th>
                <th className="editorial-label px-3 py-3">stage</th>
                <th className="editorial-label px-3 py-3">issues</th>
              </tr>
            </thead>
            <tbody>
              {preview.rows.map((r) => (
                <tr key={r.row} className={`border-b border-taupe/30 ${r.errors.length ? "bg-red-50/40" : ""}`} data-testid={`preview-row-${r.row}`}>
                  <td className="px-3 py-2 text-taupe">{r.row}</td>
                  <td className="px-3 py-2 font-serif text-sm lowercase text-charcoal">{r.data.title || <span className="text-red-700">missing</span>}</td>
                  <td className="px-3 py-2 text-charcoal">{r.data.brand || "—"}</td>
                  <td className="px-3 py-2 text-charcoal tabular-nums">{formatMoney(r.data.purchase_cost || 0)}</td>
                  <td className="px-3 py-2 text-charcoal tabular-nums">{formatMoney(r.data.profit_margin || 0)}</td>
                  <td className="px-3 py-2 text-xs uppercase tracking-widest text-taupe">{r.data.stage}</td>
                  <td className="px-3 py-2 text-red-700 text-xs">{r.errors.join("; ") || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-linen border border-taupe/50 p-10" data-testid="upload-section">
      <p className="editorial-label mb-3">required columns</p>
      <p className="text-xs text-espresso mb-6 leading-relaxed">{CSV_COLUMNS.join(" · ")}</p>
      <p className="editorial-label mb-3">need a template?</p>
      <button onClick={downloadSample} data-testid="download-template" className="mb-8 border border-brass text-brass hover:bg-brass hover:text-ivory px-5 py-2.5 text-xs tracking-[0.25em] uppercase flex items-center gap-2">
        <FileSpreadsheet className="w-4 h-4" /> download csv template
      </button>
      <label className="block border-2 border-dashed border-taupe p-10 text-center cursor-pointer hover:border-brass transition-colors" data-testid="csv-drop">
        <Upload className="w-8 h-8 text-taupe mx-auto mb-3" />
        <p className="font-serif text-2xl lowercase text-charcoal">{uploading ? "parsing…" : "drop a .csv here, or click to choose"}</p>
        <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => upload(e.target.files?.[0])} disabled={uploading} />
      </label>
    </section>
  );
}

export default function AdminImport() {
  const [tab, setTab] = useState("csv");

  return (
    <AdminLayout>
      <div data-testid="admin-import" className="max-w-5xl">
        <span className="editorial-label">sourcing · bulk import</span>
        <h1 className="font-serif text-4xl sm:text-5xl lowercase text-charcoal mt-3 mb-2">a sourcing trip, in one upload.</h1>
        <p className="text-espresso max-w-2xl mb-8">drag a csv with up to a few hundred pieces, then drop a folder of photos. we match images to inventory by sku.</p>

        <div className="flex gap-2 border-b border-taupe/40 mb-8" data-testid="import-tabs">
          <button
            onClick={() => setTab("csv")}
            data-testid="tab-csv"
            className={`px-5 py-3 text-xs tracking-[0.25em] uppercase border-b-2 -mb-px flex items-center gap-2 transition-colors ${tab === "csv" ? "border-brass text-brass" : "border-transparent text-taupe hover:text-charcoal"}`}
          >
            <FileSpreadsheet className="w-4 h-4" /> csv · spec sheet
          </button>
          <button
            onClick={() => setTab("photos")}
            data-testid="tab-photos"
            className={`px-5 py-3 text-xs tracking-[0.25em] uppercase border-b-2 -mb-px flex items-center gap-2 transition-colors ${tab === "photos" ? "border-brass text-brass" : "border-transparent text-taupe hover:text-charcoal"}`}
          >
            <Images className="w-4 h-4" /> batch · photos by sku
          </button>
        </div>

        {tab === "csv" ? <CsvImport /> : <BatchPhotosImport />}
      </div>
    </AdminLayout>
  );
}
